/**
 * useWhatsNew — fetches the tip to show on this app open, and records what the
 * user did with it.
 *
 * **One tip per cooldown window.** The endpoint returns a small batch (ordered
 * by priority), but only the first is ever displayed: acting on it closes the
 * dialog and arms the per-device cooldown, so the next tip comes on a later day
 * rather than immediately after this one. A queue of cards fired back-to-back
 * reads as spam and gets dismissed reflexively; one card a day gets read.
 *
 * The one exception is `skip` — see below.
 *
 * **Personal cards** (`tip.personal` — a code-owned card addressed to this user,
 * e.g. an admin's reply to their feedback) sit outside that pacing: inside the
 * cooldown the hook still asks the server for personal cards only, acting on
 * one doesn't spend the window, and `requestPersonalTipsCheck()` fetches them
 * mid-session. See docs/FeatureDesigns/feedback-reply-popup-target.md.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { WhatsNewApi, WhatsNewTip, TipAction } from './whatsNewApi';
import { appHref } from '../tenant';

const COOLDOWN_KEY = 'whatsNew_lastShown';
const DEFAULT_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const PERSONAL_CHECK_EVENT = 'whats-new:personal-check';

/**
 * Ask the mounted What's New provider to look for personal cards now (e.g. a
 * flagged `feedback_reply` bus message just arrived). A no-op while a card is
 * already open.
 */
export function requestPersonalTipsCheck(): void {
    window.dispatchEvent(new Event(PERSONAL_CHECK_EVENT));
}

interface UseWhatsNewOptions {
    api: WhatsNewApi;
    appTarget: 'student' | 'teacher';
    /**
     * The signed-in user. REQUIRED for the fetch to run, and part of the query
     * key — the provider is mounted above the router (login page included), so
     * without this the tips call fires unauthenticated on boot, 401s, and with
     * `retry: false` + `staleTime: Infinity` never runs again for that session.
     * Signing in afterwards changed nothing, so a fresh login saw no tips at all.
     * Keying on the id also means switching accounts refetches instead of
     * reusing the previous user's tips.
     */
    userId?: string;
    platform?: string;
    appVersion?: string;
    cooldownMs?: number;
}

interface UseWhatsNewResult {
    /** Whether the dialog should be open. */
    isOpen: boolean;
    /** The tip being displayed (or null). */
    currentTip: WhatsNewTip | null;
    /** Dismiss the current tip (long snooze) and close. */
    dismiss: () => void;
    /** "Remind me later" the current tip (short snooze) and close. */
    remindLater: () => void;
    /** Terminal success for a custom experience (dismiss cooldown, recorded as 'completed') and close. */
    complete: () => void;
    /** Submit a survey response (persists answers, server marks 'completed'), then close + arm cooldown. */
    submitSurvey: (answers: Record<string, unknown>) => void;
    /** Dismiss the current tip, then navigate to `url` once the dismiss is confirmed. */
    tryNow: (url: string) => Promise<void>;
    /** Close the dialog (Escape/overlay click) — treated as "remind me later" for the current tip. */
    close: () => void;
    /** An experience found it has nothing to show. Snoozes that tip so a
     *  different one surfaces next open, but does NOT spend the cooldown window
     *  on a card the user never saw. */
    skip: () => void;
    /** Manually open the dialog (e.g., from a menu). */
    open: () => void;
    /** Record that the current tip's content is actually on screen: beacons one
     *  impression and starts the time-to-click clock. Idempotent per tip. */
    markShown: () => void;
    /** True while loading tips. */
    isLoading: boolean;
}

function isCooldownActive(cooldownMs: number): boolean {
    const last = localStorage.getItem(COOLDOWN_KEY);
    if (!last) return false;
    return Date.now() - parseInt(last, 10) < cooldownMs;
}

export function useWhatsNew(opts: UseWhatsNewOptions): UseWhatsNewResult {
    const { api, appTarget, userId, platform, appVersion, cooldownMs = DEFAULT_COOLDOWN_MS } = opts;
    const queryClient = useQueryClient();

    const [isOpen, setIsOpen] = useState(false);
    const [currentTip, setCurrentTip] = useState<WhatsNewTip | null>(null);
    const isOpenRef = useRef(false);
    isOpenRef.current = isOpen;

    // Read once per mount so the request and what it may show agree: inside the
    // cooldown, only cards addressed to this user are asked for.
    const [cooling] = useState(() => isCooldownActive(cooldownMs));

    // Effectiveness telemetry: stamp when the visible tip appeared (for
    // time-to-click) and beacon one impression per display.
    const shownAtRef = useRef(0);
    const displayedTipRef = useRef<string | null>(null);
    const dwellMs = useCallback(() => (shownAtRef.current ? Date.now() - shownAtRef.current : undefined), []);

    // Initial fetch — the server returns a priority-ordered batch; we show its head.
    const { isLoading } = useQuery({
        queryKey: ['whats-new-unseen', appTarget, userId],
        queryFn: async () => {
            const result = await api.getUnseenTips({ app: appTarget, platform, appVersion, personalOnly: cooling });
            const tip = result.tips[0] ?? null;
            setCurrentTip(tip);
            if (tip) setIsOpen(true);
            return result;
        },
        enabled: !!userId,
        staleTime: Infinity, // Only fetch once per session
        retry: false,
    });

    /**
     * One impression per tip, plus the start of the dwell clock.
     *
     * The shell decides WHEN to call this, because only it knows whether the
     * tip's content is on screen yet: a default card is content the moment it
     * renders, while an experience that loads data may still turn out to have
     * nothing and skip. Firing on selection instead would count those as shown.
     * Fire-and-forget; idempotent per tip.
     */
    const markShown = useCallback(() => {
        if (!currentTip || displayedTipRef.current === currentTip.id) return;
        displayedTipRef.current = currentTip.id;
        shownAtRef.current = Date.now();
        api.recordImpression(currentTip.id).catch(() => { /* telemetry is best-effort */ });
    }, [currentTip, api]);

    // Interaction mutation (dismiss / remind-later / try-now / close) — records
    // the real control + time-to-click. Fire-and-forget.
    const actMutation = useMutation({
        mutationFn: (v: { tipId: string; action: TipAction; dwellMs?: number }) =>
            api.act(v.tipId, v.action, v.dwellMs),
    });

    // Mid-session check for personal cards. Imperative rather than a refetch of
    // the query above: that one is the once-per-open broadcast fetch, and a
    // rising unread count must not resurface an ordinary tip.
    useEffect(() => {
        if (!userId) return;
        let inFlight = false;
        const check = async () => {
            if (isOpenRef.current || inFlight) return;
            inFlight = true;
            try {
                const result = await api.getUnseenTips({ app: appTarget, platform, appVersion, personalOnly: true });
                const tip = result.tips[0];
                if (tip && !isOpenRef.current) {
                    // The same system card may have been shown earlier this
                    // session for an older reply — let it count as shown again.
                    displayedTipRef.current = null;
                    setCurrentTip(tip);
                    setIsOpen(true);
                }
            } catch {
                /* best-effort — the next app open asks again */
            } finally {
                inFlight = false;
            }
        };
        window.addEventListener(PERSONAL_CHECK_EVENT, check);
        return () => window.removeEventListener(PERSONAL_CHECK_EVENT, check);
    }, [userId, api, appTarget, platform, appVersion]);

    /** Spend the once-a-day window — but not on a personal card, which is not a tip. */
    const armCooldown = useCallback((tip: WhatsNewTip) => {
        if (!tip.personal) localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    }, []);

    /** The user engaged with the tip: record it, close, and spend the window. */
    const actAndClose = useCallback((action: TipAction) => {
        if (!currentTip) return;
        actMutation.mutate({ tipId: currentTip.id, action, dwellMs: dwellMs() });
        setIsOpen(false);
        armCooldown(currentTip);
    }, [currentTip, actMutation, dwellMs, armCooldown]);

    const dismiss = useCallback(() => actAndClose('dismiss'), [actAndClose]);
    const remindLater = useCallback(() => actAndClose('remind-later'), [actAndClose]);
    // A custom experience finished successfully — long (dismiss) cooldown, recorded as 'completed'.
    const complete = useCallback(() => actAndClose('completed'), [actAndClose]);

    // Survey submit: post the answers (server persists them AND records the tip
    // 'completed'), then close and arm the local cooldown. Fire-and-forget on the
    // network like the other actions — the modal shouldn't block on the round trip.
    const surveyMutation = useMutation({
        mutationFn: (v: { tipId: string; answers: Record<string, unknown>; dwellMs?: number }) =>
            api.submitSurvey(v.tipId, v.answers, v.dwellMs),
    });
    const submitSurvey = useCallback((answers: Record<string, unknown>) => {
        if (!currentTip) return;
        surveyMutation.mutate({ tipId: currentTip.id, answers, dwellMs: dwellMs() });
        setIsOpen(false);
        armCooldown(currentTip);
    }, [currentTip, surveyMutation, dwellMs, armCooldown]);
    // Implicit close (Escape / overlay click) — a soft remind-later cooldown,
    // but recorded distinctly as 'close' for analytics.
    const close = useCallback(() => actAndClose('close'), [actAndClose]);

    /**
     * An experience mounted, found it has no data for this user, and wants out.
     *
     * It still snoozes the tip — otherwise the same empty card would be the head
     * of the batch on every open and no other tip could ever surface — but it
     * deliberately does NOT arm the per-device cooldown. Nothing was shown, so
     * nothing should be spent: a student with thin practice data would otherwise
     * have one silent experience eat their What's New slot every single day.
     */
    const skip = useCallback(() => {
        if (!currentTip) return;
        actMutation.mutate({ tipId: currentTip.id, action: 'remind-later' });
        setIsOpen(false);
    }, [currentTip, actMutation]);

    // "Try Now" — record the try-now engagement (long cooldown, server-derived),
    // then navigate. Awaits the call before navigating so the request isn't
    // aborted by the page unload that follows a same-tab redirect.
    const tryNow = useCallback(async (url: string) => {
        if (!currentTip) {
            window.location.href = url.startsWith('/') ? appHref(url) : url;
            return;
        }
        try {
            await actMutation.mutateAsync({ tipId: currentTip.id, action: 'try-now', dwellMs: dwellMs() });
            armCooldown(currentTip);
        } finally {
            window.location.href = url.startsWith('/') ? appHref(url) : url;
        }
    }, [currentTip, actMutation, dwellMs, armCooldown]);

    const open = useCallback(() => {
        // Manually open — bypass cooldown, refetch if we have nothing loaded.
        if (currentTip) setIsOpen(true);
        else queryClient.invalidateQueries({ queryKey: ['whats-new-unseen', appTarget, userId] });
    }, [currentTip, queryClient, appTarget, userId]);

    return {
        isOpen,
        currentTip,
        dismiss,
        remindLater,
        complete,
        submitSurvey,
        tryNow,
        close,
        skip,
        open,
        markShown,
        isLoading,
    };
}
